// src/features/analytics/useAnalyticsOverview.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type AnalyticsViewScope } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, OverviewData } from './types';

export interface UseAnalyticsOverviewResult {
  overview: AnalyticsEnvelope<OverviewData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAnalyticsOverview(
  scope: AnalyticsViewScope | null,
  range: UtcRange
): UseAnalyticsOverviewResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.overview(
      scope?.kind ?? 'account',
      scope?.cid ?? 0,
      range.from,
      range.to
    ),
    queryFn: () => AnalyticsApi.getOverview(scope!, range),
    enabled: scope !== null,
    staleTime: 60_000,
  });

  return {
    overview: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
